import type { JobSummary } from '@/types'
import { getJobNodeDisplayName, getJobNodeSecondaryText, shortJobId } from '@/utils/jobs'
import { formatKstDateTime } from '@/utils/datetime'
import JobStatusBadge from './JobStatusBadge'

interface Props {
  jobs: JobSummary[]
  onRowClick: (job: JobSummary) => void
  onCancel?: (job: JobSummary) => void
  loading?: boolean
}

function formatTime(ts: string | null | undefined): string {
  return formatKstDateTime(ts, '—')
}

function ProgressBar({ value }: { value: number | null }) {
  if (value == null) return <span style={{ color: '#aaa', fontSize: 12 }}>—</span>
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 80 }}>
      <div
        style={{
          flex: 1,
          height: 5,
          background: '#e0e0e0',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(value, 100)}%`,
            height: '100%',
            background: '#3949ab',
            borderRadius: 3,
          }}
        />
      </div>
      <span style={{ fontSize: 11, color: '#555', minWidth: 28, textAlign: 'right' }}>
        {value.toFixed(0)}%
      </span>
    </div>
  )
}

function TypeTag({ type }: { type: string }) {
  const isRecording = type === 'recording'
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 700,
        background: isRecording ? '#fce4ec' : '#e8eaf6',
        color: isRecording ? '#c62828' : '#3949ab',
        textTransform: 'uppercase',
        letterSpacing: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {type}
    </span>
  )
}

function EllipsisText({
  value,
  title,
  mono,
  color,
  fontWeight,
  fontSize = 12,
}: {
  value: string | null | undefined
  title?: string
  mono?: boolean
  color?: string
  fontWeight?: number
  fontSize?: number
}) {
  const hasValue = value != null && value !== '' && value !== '—'
  const displayValue = hasValue ? value : '—'
  return (
    <div
      title={title ?? (hasValue ? value : undefined)}
      style={{
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: color ?? (hasValue ? '#333' : '#aaa'),
        fontFamily: mono ? 'monospace' : undefined,
        fontSize,
        fontWeight,
      }}
    >
      {displayValue}
    </div>
  )
}

function NodeCell({ job }: { job: JobSummary }) {
  const primary = getJobNodeDisplayName(job)
  const secondary = getJobNodeSecondaryText(job)
  return (
    <div style={{ minWidth: 0 }}>
      <EllipsisText value={primary} title={job.node_id} fontWeight={600} fontSize={13} />
      {secondary && (
        <EllipsisText value={secondary} title={secondary} color="#888" fontSize={11} />
      )}
    </div>
  )
}

const CANCELLABLE = new Set(['queued', 'running'])
const COLUMN_WIDTHS = ['7%', '15%', '7%', '7%', '8%', '9%', '11%', '11%', '7%', '12%', '6%']

export default function JobTable({ jobs, onRowClick, onCancel, loading }: Props) {
  if (loading && jobs.length === 0) {
    return (
      <div style={{ padding: '20px 0', color: '#888', fontSize: 13 }}>
        Loading jobs...
      </div>
    )
  }

  if (jobs.length === 0) {
    return (
      <div style={{ padding: '20px 0', color: '#aaa', fontSize: 13 }}>
        No jobs found.
      </div>
    )
  }

  // Both fields are always present in JobSummary (nullable string, cycle 6+)
  // Always show the columns so the table structure is consistent
  const hasRequestedBy = true
  const hasErrorMessage = true

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <colgroup>
          {COLUMN_WIDTHS.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th style={thStyle}>Job ID</th>
            <th style={thStyle}>Node</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Camera</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Progress</th>
            <th style={thStyle}>Started</th>
            <th style={thStyle}>Finished</th>
            {hasRequestedBy && <th style={thStyle}>Requested by</th>}
            {hasErrorMessage && <th style={thStyle}>Error</th>}
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.id}
              style={trStyle}
              onClick={() => onRowClick(job)}
            >
              <td style={tdStyle}>
                <EllipsisText value={shortJobId(job.id)} title={job.id} mono />
              </td>
              <td style={tdStyle}>
                <NodeCell job={job} />
              </td>
              <td style={tdStyle}>
                <TypeTag type={job.type} />
              </td>
              <td style={tdStyle}>
                <EllipsisText value={job.camera_key} mono color="#555" />
              </td>
              <td style={tdStyle}>
                <JobStatusBadge status={job.status} />
              </td>
              <td style={tdStyle}>
                <ProgressBar value={job.progress} />
              </td>
              <td style={tdStyle}>
                <EllipsisText value={formatTime(job.started_at)} title={job.started_at ?? undefined} />
              </td>
              <td style={tdStyle}>
                <EllipsisText value={formatTime(job.finished_at)} title={job.finished_at ?? undefined} />
              </td>
              {hasRequestedBy && (
                <td style={tdStyle}>
                  <EllipsisText value={job.requested_by} color="#888" />
                </td>
              )}
              {hasErrorMessage && (
                <td style={tdStyle}>
                  <EllipsisText value={job.error_message} title={job.error_message ?? undefined} color={job.error_message ? '#c62828' : '#aaa'} />
                </td>
              )}
              <td style={tdStyle}>
                {onCancel && CANCELLABLE.has(job.status) && (
                  <button
                    style={cancelBtnStyle}
                    onClick={(e) => {
                      e.stopPropagation()
                      onCancel(job)
                    }}
                  >
                    Cancel
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  fontSize: 13,
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '2px solid #e0e0e0',
  fontWeight: 700,
  fontSize: 11,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: 0,
  whiteSpace: 'nowrap',
}

const trStyle: React.CSSProperties = {
  cursor: 'pointer',
  borderBottom: '1px solid #f0f0f0',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: '#333',
  verticalAlign: 'middle',
  overflow: 'hidden',
}

const cancelBtnStyle: React.CSSProperties = {
  padding: '3px 10px',
  background: '#fff',
  border: '1px solid #e53935',
  borderRadius: 4,
  color: '#e53935',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
